import { Outlet, NavLink, useParams, Navigate } from 'react-router-dom';
import { Settings, HomeIcon, CreditCardIcon, CalendarDays } from 'lucide-react';

import { cn } from '@/utils/cn';
import { useGymStore } from '@/store/gym-store';

interface NavItem {
  icon: React.ReactNode;
  label: string;
  to: string;
}

export function GymLayout() {
  const { gymId } = useParams<{ gymId: string }>();
  const { selectedGymId, setSelectedGymId, gyms } = useGymStore();

  // Update selected gym in store when navigating to a gym
  if (gymId && gymId !== selectedGymId) {
    setSelectedGymId(gymId);
  }

  // If no gymId in URL, redirect to first gym's settings or create new gym
  if (!gymId) {
    if (gyms.length > 0) {
      return <Navigate to={`/app/${gyms[0].gymId}/settings`} replace />;
    }
    return <Navigate to="/app/gym/new" replace />;
  }

  const navItems: NavItem[] = [
    {
      icon: <HomeIcon className="w-6 h-6" />,
      label: 'Home',
      to: `/app/${gymId}/home`,
    },
    {
      icon: <CalendarDays className="w-6 h-6" />,
      label: 'Slot Manager',
      to: `/app/${gymId}/slot-manager`,
    },
    {
      icon: <CreditCardIcon className="w-6 h-6" />,
      label: 'Earnings',
      to: `/app/${gymId}/earnings`,
    },
    {
      icon: <Settings className="w-6 h-6" />,
      label: 'Settings',
      to: `/app/${gymId}/settings`,
    },
  ];

  return (
    <div className="min-h-screen bg-surface-base flex flex-col">
      {/* Main Content */}
      <main className="flex-1 pb-20">
        <Outlet />
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-surface-elevated border-t border-border-default safe-area-bottom">
        <div className="flex items-center justify-around h-16">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'flex flex-col items-center justify-center gap-1 px-4 py-2 min-w-[64px]',
                  'transition-colors duration-200',
                  isActive
                    ? 'text-primary-accent'
                    : 'text-foreground-secondary hover:text-foreground-primary'
                )
              }
            >
              {item.icon}
              <span className="text-body-x-small font-medium">{item.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

export default GymLayout;
